import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { DeliveryZone } from '../../types';

type DeliveryZoneMapEditorProps = {
  zones: DeliveryZone[];
  center: { lat: number; lng: number };
  onZonesChange: (zones: DeliveryZone[]) => void;
};

type LatLngPoint = { lat: number; lng: number };

declare global {
  interface Window {
    google?: any;
    __garcomGoogleMapsPromise?: Promise<void>;
  }
}

const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const loadGoogleMaps = () => {
  if (window.google?.maps) return Promise.resolve();
  if (window.__garcomGoogleMapsPromise) return window.__garcomGoogleMapsPromise;

  if (!googleMapsApiKey) {
    return Promise.reject(new Error('REACT_APP_GOOGLE_MAPS_API_KEY nao configurada'));
  }

  window.__garcomGoogleMapsPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>('script[data-garcom-google-maps="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', () => reject(new Error('Falha ao carregar Google Maps')));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(googleMapsApiKey)}&language=pt-BR&region=BR`;
    script.async = true;
    script.defer = true;
    script.dataset.garcomGoogleMaps = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Falha ao carregar Google Maps'));
    document.head.appendChild(script);
  });

  return window.__garcomGoogleMapsPromise;
};

const extractPolygonPath = (geojson: Record<string, unknown>): LatLngPoint[] => {
  const feature = geojson as any;
  const geometry = feature.type === 'Feature' ? feature.geometry : feature;
  const coordinates = geometry?.coordinates?.[0];

  if (!Array.isArray(coordinates)) return [];

  return coordinates
    .map((coordinate: unknown) => {
      if (!Array.isArray(coordinate) || coordinate.length < 2) return null;
      const lng = Number(coordinate[0]);
      const lat = Number(coordinate[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { lat, lng };
    })
    .filter(Boolean) as LatLngPoint[];
};

const buildPolygonGeoJson = (path: LatLngPoint[]) => {
  const coordinates = path.map((point) => [point.lng, point.lat]);
  const first = coordinates[0];
  const last = coordinates[coordinates.length - 1];

  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    coordinates.push(first);
  }

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coordinates],
    },
    properties: {},
  };
};

const readGooglePath = (polygon: any): LatLngPoint[] => {
  const path = polygon.getPath();
  const points: LatLngPoint[] = [];
  for (let index = 0; index < path.getLength(); index += 1) {
    const item = path.getAt(index);
    points.push({ lat: item.lat(), lng: item.lng() });
  }
  return points;
};

const DeliveryZoneMapEditor: React.FC<DeliveryZoneMapEditorProps> = ({ zones, center, onZonesChange }) => {
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const restaurantMarkerRef = useRef<any>(null);
  const zonePolygonsRef = useRef<Map<string, any>>(new Map());
  const draftPolygonRef = useRef<any>(null);
  const draftMarkersRef = useRef<any[]>([]);
  const zonesRef = useRef<DeliveryZone[]>(zones);
  const onZonesChangeRef = useRef(onZonesChange);

  const [mapError, setMapError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [draftPoints, setDraftPoints] = useState<LatLngPoint[]>([]);

  const normalizedCenter = useMemo(
    () => ({
      lat: Number.isFinite(center.lat) ? center.lat : -19.9167,
      lng: Number.isFinite(center.lng) ? center.lng : -43.9345,
    }),
    [center.lat, center.lng],
  );

  useEffect(() => {
    zonesRef.current = zones;
  }, [zones]);

  useEffect(() => {
    onZonesChangeRef.current = onZonesChange;
  }, [onZonesChange]);

  useEffect(() => {
    if (!mapElementRef.current || mapRef.current) return;

    loadGoogleMaps()
      .then(() => {
        const google = window.google;
        if (!google?.maps || !mapElementRef.current) {
          throw new Error('Google Maps indisponivel');
        }

        const map = new google.maps.Map(mapElementRef.current, {
          center: normalizedCenter,
          zoom: 14,
          mapTypeControl: false,
          fullscreenControl: true,
          streetViewControl: false,
          clickableIcons: false,
        });

        restaurantMarkerRef.current = new google.maps.Marker({
          map,
          position: normalizedCenter,
          title: 'Restaurante',
        });

        map.addListener('click', (event: any) => {
          if (!event.latLng) return;
          setDraftPoints((current) => {
            if (!draftPolygonRef.current) return current;
            const next = [...current, { lat: event.latLng.lat(), lng: event.latLng.lng() }];
            draftPolygonRef.current.setPath(next);

            const marker = new google.maps.Marker({
              map,
              position: event.latLng,
              label: String(next.length),
            });
            draftMarkersRef.current.push(marker);

            return next;
          });
        });

        mapRef.current = map;
        setMapReady(true);
      })
      .catch((error) => {
        setMapError(error instanceof Error ? error.message : 'Nao foi possivel carregar o Google Maps.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mapRef.current || !restaurantMarkerRef.current) return;
    mapRef.current.setCenter(normalizedCenter);
    restaurantMarkerRef.current.setPosition(normalizedCenter);
  }, [normalizedCenter]);

  useEffect(() => {
    const google = window.google;
    if (!mapRef.current || !google?.maps || !mapReady) return;

    const renderedPolygons = zonePolygonsRef.current;
    renderedPolygons.forEach((polygon) => polygon.setMap(null));
    renderedPolygons.clear();

    const bounds = new google.maps.LatLngBounds();
    let hasBounds = false;

    zones.forEach((zone) => {
      const path = extractPolygonPath(zone.geojson);
      if (path.length < 3) return;

      const polygon = new google.maps.Polygon({
        map: mapRef.current,
        paths: path,
        editable: true,
        draggable: false,
        strokeColor: zone.isActive ? '#f97316' : '#6b7280',
        strokeOpacity: 0.95,
        strokeWeight: 2,
        fillColor: zone.isActive ? '#f97316' : '#6b7280',
        fillOpacity: zone.isActive ? 0.22 : 0.08,
      });

      path.forEach((point) => {
        bounds.extend(point);
        hasBounds = true;
      });

      const syncPolygon = () => {
        const nextPath = readGooglePath(polygon);
        onZonesChangeRef.current(
          zonesRef.current.map((currentZone) => (
            currentZone.id === zone.id
              ? { ...currentZone, geojson: buildPolygonGeoJson(nextPath) }
              : currentZone
          )),
        );
      };

      const polygonPath = polygon.getPath();
      google.maps.event.addListener(polygonPath, 'set_at', syncPolygon);
      google.maps.event.addListener(polygonPath, 'insert_at', syncPolygon);
      google.maps.event.addListener(polygonPath, 'remove_at', syncPolygon);

      renderedPolygons.set(zone.id, polygon);
    });

    if (hasBounds && zones.length > 0) {
      mapRef.current.fitBounds(bounds, 48);
    }

    return () => {
      renderedPolygons.forEach((polygon) => polygon.setMap(null));
      renderedPolygons.clear();
    };
  }, [mapReady, zones]);

  const clearDraft = () => {
    draftPolygonRef.current?.setMap(null);
    draftPolygonRef.current = null;
    draftMarkersRef.current.forEach((marker) => marker.setMap(null));
    draftMarkersRef.current = [];
    setDraftPoints([]);
    setIsDrawing(false);
  };

  const startDrawing = () => {
    const google = window.google;
    if (!mapRef.current || !google?.maps) return;

    clearDraft();
    const polygon = new google.maps.Polygon({
      map: mapRef.current,
      paths: [],
      editable: false,
      strokeColor: '#22c55e',
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: '#22c55e',
      fillOpacity: 0.18,
    });

    draftPolygonRef.current = polygon;
    setIsDrawing(true);
  };

  const undoLastPoint = () => {
    setDraftPoints((current) => {
      const next = current.slice(0, -1);
      draftMarkersRef.current.pop()?.setMap(null);
      draftPolygonRef.current?.setPath(next);
      return next;
    });
  };

  const finishDraft = () => {
    if (draftPoints.length < 3) return;

    const nextZone: DeliveryZone = {
      id: `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name: `Area ${zonesRef.current.length + 1}`,
      geojson: buildPolygonGeoJson(draftPoints),
      deliveryFee: 0,
      isActive: true,
    };

    onZonesChangeRef.current([...zonesRef.current, nextZone]);
    clearDraft();
  };

  if (!googleMapsApiKey) {
    return (
      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5 text-sm leading-6 text-amber-100">
        Configure <span className="font-semibold">REACT_APP_GOOGLE_MAPS_API_KEY</span> no frontend para usar o Google Maps
        no desenho das areas de entrega.
      </div>
    );
  }

  if (mapError) {
    return (
      <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-5 text-sm leading-6 text-red-100">
        {mapError}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-[#2A2A2A] bg-[#0f1116]">
      <div className="flex flex-col gap-3 border-b border-[#2A2A2A] bg-[#151515] p-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-semibold text-white">Desenhe a cobertura no Google Maps</p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Clique em Nova area, marque os cantos da regiao e finalize. Depois, arraste os pontos do poligono para ajustar.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isDrawing ? (
            <button type="button" onClick={startDrawing} disabled={!mapReady} className="btn-primary">
              Nova area
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={undoLastPoint}
                disabled={draftPoints.length === 0}
                className="rounded-xl border border-[#2A2A2A] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                Desfazer ponto
              </button>
              <button
                type="button"
                onClick={clearDraft}
                className="rounded-xl border border-[#2A2A2A] px-4 py-2 text-sm font-semibold text-white"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={finishDraft}
                disabled={draftPoints.length < 3}
                className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
              >
                Concluir area
              </button>
            </>
          )}
        </div>
      </div>

      {isDrawing && (
        <div className="border-b border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
          Marcando area: {draftPoints.length} ponto(s). Use pelo menos 3 pontos para fechar a cobertura.
        </div>
      )}

      <div ref={mapElementRef} className="h-[520px] w-full 2xl:h-[720px]" />
    </div>
  );
};

export default DeliveryZoneMapEditor;
